import React, { useState, useEffect } from 'react';
import ChallengeQuestionList from './ChallengeQuestionList';
import LLMChallengePopup from './LLMChallengePopup';
import CriteriaView from './Shards/CriteriaView';
// import ImprovementsView from './Shards/ImprovementsView';
import CheatingProbabilityIndicator from './Shards/CheatingProbabilityIndicator';
import CheatingDetailsModal from '../CheatingComponents/CheatingDetailsModal';
import { useSession } from '../Utils/SessionContext';
import { 
  getQuizScoreTextColorClass
} from '../Utils/ColorUtils';
import ChallengeStatus from './Utils/ChallengeStatus';

/**
 * EvalChallenge Component
 * Reusable component for displaying challenge responses for both talents and candidates
 */
const EvalChallenge = ({ 
  challengeData,
  contextType = 'talent' // 'talent' or 'candidate'
}) => {
  const { user } = useSession();
  // State to store parsed data
  const [parsedData, setParsedData] = useState({
    questions: [],
    duration: null,
    status: null,
    challengeType: null,
    cheatingEvents: [],
    cheatingAssessment: null,
    totalScore: null
  });
  
  // State for popups
  const [showLlmPopup, setShowLlmPopup] = useState(false);
  const [showCriteriaPopup, setShowCriteriaPopup] = useState(false);
  // const [showImprovementsPopup, setShowImprovementsPopup] = useState(false);
  const [showCheatingModal, setShowCheatingModal] = useState(false);
  
  // Function to parse and prepare challenge data
  const prepareChallengeData = () => {
    if (!challengeData) {
      return;
    }
    
    console.log("challengeData:: ", challengeData);
    
    // Extract questions from challenge_final_json or challenge_copy_json if no answers yet
    const questions = challengeData.challenge_final_json?.questions?.length > 0 
      ? challengeData.challenge_final_json.questions 
      : (challengeData.challenge_copy_json?.questions || []);
    
    // Calculate actual completion duration only if both start and end dates exist
    let completionDuration = null;
    if (challengeData.challenge_start_date && challengeData.challenge_end_date) {
      const startDate = new Date(challengeData.challenge_start_date);
      const endDate = new Date(challengeData.challenge_end_date);
      const durationMs = endDate - startDate;
      completionDuration = Math.round(durationMs / (1000 * 60)); // Convert to minutes
    }
    
    // Get max duration separately
    let maxDuration = null;
    if (challengeData.max_duration_minutes) {
      maxDuration = parseInt(challengeData.max_duration_minutes);
    }
    
    // Extract status
    const status = challengeData.challenge_status || null;
    
    // Extract challenge type
    const challengeType = challengeData.challenges?.challenge_type || null;
    
    // Extract cheating events
    const cheatingEvents = challengeData.challenge_cheating_events_json || [];
    
    // Extract cheating assessment
    const cheatingAssessment = challengeData.challenge_cheating_assessment_json || null;
    
    // Extract total score if available
    // const totalScore = challengeData.challenge_assessed_json?.totalScore ?? null;
    const totalScore = challengeData.challenge_final_json?.totalScore ?? null;
    
    setParsedData({
      questions,
      completionDuration,
      maxDuration,
      status,
      challengeType,
      cheatingEvents,
      cheatingAssessment,
      totalScore
    });
  };
  
  // Parse data when challengeData changes
  useEffect(() => {
    prepareChallengeData();
  }, [challengeData]);
  
  // Dummy function for onRegenerateQuestion since we don't need regeneration functionality
  const handleRegenerateQuestion = () => {
    // This function is required by ChallengeQuestionList but won't do anything in this context
  };
  
  // Format duration for display
  const formatDuration = (minutes) => {
    if (!minutes && minutes !== 0) return 'Not available';
    
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    
    if (hours === 0) {
      return `${minutes}m`;
    } else if (remainingMinutes === 0) {
      return `${hours}h`;
    } else {
      return `${hours}h ${remainingMinutes}m`;
    }
  };
  

  return (
    <div className="space-y-4">
      {/* Tab-related header */}
      <div className="bg-white shadow-sm rounded-lg p-6 mb-6 border border-gray-200">
        <div className="flex justify-between items-start">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <h2 className="text-2xl font-bold text-gray-900">Challenge Response</h2>
              
              {/* Integrity Report - standalone button */}
              {parsedData.cheatingAssessment && (
                <button
                  onClick={() => setShowCheatingModal(true)}
                  className="h-8 px-3 text-sm bg-gradient-to-r from-green-500 to-red-500 text-white rounded hover:from-green-600 hover:to-red-600 transition-colors flex items-center whitespace-nowrap"
                >
                  <svg className="w-3.5 h-3.5 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
                  </svg>
                  Integrity Report
                </button>
              )}
              
              {/* Actions Dropdown - 3 dots icon */}
              <div className="relative group">
                <button className="h-8 px-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded hover:border-gray-400 transition-colors flex items-center">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/>
                  </svg>
                </button>
                
                {/* Dropdown Menu */}
                <div className="absolute right-0 mt-1 w-64 bg-white border border-gray-200 rounded-md shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
                  <div className="py-1">
                    {/* View Criteria */}
                    <button
                      onClick={() => setShowCriteriaPopup(true)}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 flex items-center"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 616 0z"></path>
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                      </svg>
                      View Criteria
                    </button>
                    
                    {/* Solve with LLM - only visible for Superadmin */}
                    {user?.role === 'Superadmin' && (
                      <>
                        <div className="border-t border-gray-200 my-1"></div>
                        <button
                          onClick={() => setShowLlmPopup(true)}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 flex items-center"
                        >
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
                          </svg>
                          Solve with LLM
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <p className="text-gray-600 text-l">
              Responses to the challenge that evaluates the {contextType}'s skills.
            </p>
            <div className="flex flex-wrap items-center gap-2 mt-3">
              {/* Status first */}
              {parsedData.status && (
                <ChallengeStatus status={parsedData.status} />
              )}
              
              {/* Vertical divider */}
              {parsedData.status && (
                <div className="h-6 w-px bg-gray-300"></div>
              )}
              
              {/* Challenge type */}
              {parsedData.challengeType && (
                <div className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-purple-100 text-purple-800">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                  </svg>
                  <span className="font-medium">Type:&nbsp;</span> {parsedData.challengeType}
                </div>
              )}
              
              {/* Show max duration if available */}
              {parsedData.maxDuration !== null && (
                <div className="inline-flex items-center bg-gray-700 text-white px-3 py-1 rounded-full text-sm">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="font-medium">Max:&nbsp;</span> {parsedData.maxDuration}m
                </div>
              )}
              
              {/* Show completion time if available */}
              {parsedData.completionDuration !== null && (
                <div className="inline-flex items-center bg-black text-white px-3 py-1 rounded-full text-sm">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="font-medium">Time:&nbsp;</span> {formatDuration(parsedData.completionDuration)}
                </div>
              )}
              
              {/* Cheating Probability */}
              {challengeData?.final_cheating_analysis && (
                <CheatingProbabilityIndicator 
                  probability={Math.round(challengeData.final_cheating_analysis.final_cheating_probability * 100)}
                  rationale={challengeData.final_cheating_analysis.rationale}
                  onClick={() => setShowCheatingModal(true)}
                />
              )}
            </div>
          </div>
          
          {/* Score display - only show if score exists */}
          {parsedData.questions && parsedData.questions.length > 0 && parsedData.totalScore !== null && (
            <div className="flex flex-col items-center p-4">
              <div className="text-center">
                <h3 className="text-lg font-medium text-gray-700">Score</h3>
                <p className={`text-5xl font-bold ${getQuizScoreTextColorClass(parseFloat(parsedData.totalScore))}`}>
                  {parsedData.totalScore}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Questions or Empty State */}
      {parsedData.questions && parsedData.questions.length > 0 ? (
        <ChallengeQuestionList 
          questions={parsedData.questions} 
          onRegenerateQuestion={handleRegenerateQuestion}
          showAnswers={true}
        />
      ) : (
        <div className="p-4 bg-gray-50 rounded-lg">
          <p className="text-gray-600">Challenge response data will be displayed here.</p>
        </div>
      )}
      
      {/* LLM Challenge Popup */}
      <LLMChallengePopup
        show={showLlmPopup}
        title="LLM Challenge Evaluation"
        eval_id={challengeData?.eval_id}
        onSubmit={(success) => {
          setShowLlmPopup(false);
          
          // Show a JavaScript alert instead of a popup
          if (success) {
            alert("Challenge completed successfully! Please refresh the page in ~5 minutes to see the results.");
          } else {
            alert("Failed to complete challenge. Please try again.");
          }
        }}
        onCancel={() => setShowLlmPopup(false)}
      />
      
      {/* Criteria View Popup */}
      <CriteriaView
        show={showCriteriaPopup}
        onClose={() => setShowCriteriaPopup(false)}
        criteria_json={challengeData?.challenges?.challenge_criteria_json}
      />
      
      {/* Improvements View Popup */}
      {/* <ImprovementsView
        show={showImprovementsPopup}
        onClose={() => setShowImprovementsPopup(false)}
        questions={parsedData.questions}
      /> */}
      
      {/* Cheating Details Modal */}
      <CheatingDetailsModal
        show={showCheatingModal}
        onClose={() => setShowCheatingModal(false)}
        cheatingData={{
          cheating_assessment_json: challengeData?.challenge_cheating_assessment_json,
          cheating_events_json: challengeData?.challenge_cheating_events_json,
          final_cheating_analysis: challengeData?.final_cheating_analysis,
          voice_analysis_assessment_json: challengeData?.voice_analysis_assessment_json
        }}
        completionTime={parsedData.completionDuration}
      />
    </div>
  );
};

export default EvalChallenge;
